/**
 * RequestsList Component
 *
 * List display of advisory requests with loading and empty states
 */

import React from 'react';

import { Box, Pagination, Skeleton } from '@mui/material';

import { ViewMode } from './FiltersBar';
import RequestCard from './RequestCard';
import * as S from './styles';
import Text from 'components/Text';
import { LeadRequestWithUserData } from 'types/leadRequestsTypes';

interface RequestsListProps {
  requests: LeadRequestWithUserData[];
  loading: boolean;
  error: string | null;
  onViewDetails: (request: LeadRequestWithUserData) => void;
  onCreateLink: (request: LeadRequestWithUserData) => void;
  viewMode: ViewMode;
  totalItems: number;
  currentPage: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

const LoadingSkeleton: React.FC<{ viewMode: ViewMode }> = ({ viewMode }) => (
  <S.SkeletonContainer>
    {[1, 2, 3, 4, 5, 6].map(index => (
      <S.LoadingSkeletonCard key={index}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Skeleton variant="text" width={200} height={32} />
          <Skeleton variant="rounded" width={80} height={24} />
        </Box>
        <Box mb={2}>
          <Skeleton variant="text" width="100%" height={20} />
          <Skeleton variant="text" width="80%" height={20} />
          <Skeleton variant="text" width="60%" height={20} />
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" pt={2}>
          <Skeleton variant="text" width={120} height={16} />
          <Box display="flex" gap={1}>
            <Skeleton variant="rounded" width={90} height={32} />
            <Skeleton variant="rounded" width={100} height={32} />
          </Box>
        </Box>
      </S.LoadingSkeletonCard>
    ))}
  </S.SkeletonContainer>
);

const RequestsList: React.FC<RequestsListProps> = ({
  requests,
  loading,
  error,
  onViewDetails,
  onCreateLink,
  viewMode,
  totalItems,
  currentPage,
  itemsPerPage,
  onPageChange,
}) => {
  if (loading) {
    return <LoadingSkeleton viewMode={viewMode} />;
  }

  if (error) {
    return (
      <S.EmptyStateContainer>
        <S.EmptyStateIllustration>⚠️</S.EmptyStateIllustration>
        <Text size="large" color="red" bold>
          Erro ao carregar solicitações
        </Text>
        <Text size="medium" color="textSecondary">
          {error}
        </Text>
      </S.EmptyStateContainer>
    );
  }

  if (requests.length === 0) {
    return (
      <S.EmptyStateContainer>
        <S.EmptyStateIllustration>📋</S.EmptyStateIllustration>
        <Text size="large" bold>
          Nenhuma solicitação de consultoria encontrada
        </Text>
        <Text size="medium" color="textSecondary">
          As solicitações de consultoria aparecerão aqui quando os patrocinadores as submeterem
        </Text>
      </S.EmptyStateContainer>
    );
  }

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  return (
    <>
      <S.RequestsGrid viewMode={viewMode}>
        {requests.map(request => (
          <RequestCard
            key={request.id}
            request={request}
            onViewDetails={onViewDetails}
            onCreateLink={onCreateLink}
          />
        ))}
      </S.RequestsGrid>

      {totalPages > 1 && (
        <S.PaginationContainer>
          <S.PaginationInfo>
            Mostrando {startIndex + 1}-{endIndex} de {totalItems} solicitações
          </S.PaginationInfo>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => onPageChange(page)}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
            sx={{
              '& .MuiPaginationItem-root': {
                color: '#fff',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 199, 0, 0.2)',
                  borderColor: '#FFC700',
                  color: '#FFC700',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 199, 0, 0.3)',
                  },
                },
              },
            }}
          />
        </S.PaginationContainer>
      )}
    </>
  );
};

export default RequestsList;
